import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";
import { type PriceRankItem } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { SelectField } from "@/components/ui/form/fields/select-field";
import ArrowRightIcon from "@/components/ui/icon/arrow-right";

import { useRoomPriceCalendar } from "./hooks/use-room-price-calendar";
import { RoomPriceRankCalendarDailyStatusItem } from "./item";

import type { ProductRoomPriceRankDailyStatusFormModel } from "../../../model/price-rank-and-daily-status/_shared/daily-stasus/type";
import type { ProductRoomPriceRankDailyStatusOnSetViewModelType } from "../../../model/price-rank-and-daily-status/_shared/daily-stasus/use-case/use-room-price-rank-daily-status-model";

const WeekDays = memo(() => {
	// 日曜 ~ 土曜までの曜日（日本語）
	const weekDays = ["日", "月", "火", "水", "木", "金", "土"] as const;

	return (
		<>
			{weekDays.map((day) => (
				<div key={day} className="mb-1.5 py-0.5">
					<p
						className={cn(
							"text-center text-xs laptop:text-sm",
							day === "土" ? "text-edited" : day === "日" ? "text-not-entered" : "text-zinc-600",
						)}
					>
						{day}
					</p>
				</div>
			))}
		</>
	);
});

type Props = {
	dailyStatus: ProductRoomPriceRankDailyStatusFormModel["ViewModel"];
	onSetDailyStatus: ProductRoomPriceRankDailyStatusOnSetViewModelType;
	refetchRoomPriceRankDailyStatus: ({ startDt, endDt }: { startDt: string; endDt: string }) => Promise<void>;

	selectedPriceRank: PriceRankItem | undefined;

	isDirty: boolean;
};

export const RoomPriceRankCalendar = ({
	selectedPriceRank,
	dailyStatus,
	onSetDailyStatus,
	refetchRoomPriceRankDailyStatus,
	isDirty,
}: Props) => {
	const {
		currentYear, //
		currentMonth,

		dailyStatuses,

		selectableYears,
		handleSelectedYearChange,
		selectableMonths,
		handleSelectedMonthChange,

		handleClickNextMonth,
		handleClickPrevMonth,

		onSetPriceRank,
	} = useRoomPriceCalendar({
		dailyStatus,
		onSetDailyStatus,
		refetchRoomPriceRankDailyStatus,
		isDirty,
	});

	return (
		<div className="flex w-full min-w-0 flex-col items-center gap-y-5 laptop:w-auto laptop:gap-y-6">
			<div className="w-full">
				<div className="flex items-center justify-between gap-x-2 rounded-md px-1 laptop:mx-6 laptop:gap-x-3 laptop:px-0">
					<Button type="button" variant="input-ish" onClick={handleClickPrevMonth}>
						<ArrowRightIcon size="sm" className="rotate-180" />
					</Button>
					<div className="flex gap-x-2 laptop:gap-x-4">
						<div className="flex items-center gap-x-1">
							<SelectField
								value={currentYear}
								options={selectableYears}
								handleValueChange={handleSelectedYearChange}
								className="min-w-0 laptop:min-w-[110px]"
							/>
							<p>年</p>
						</div>
						<div className="flex items-center gap-x-1">
							<SelectField
								value={currentMonth}
								options={selectableMonths}
								handleValueChange={handleSelectedMonthChange}
								className="min-w-0 laptop:min-w-[110px]"
							/>
							<p>月</p>
						</div>
					</div>
					<Button type="button" variant="input-ish" onClick={handleClickNextMonth}>
						<ArrowRightIcon size="sm" />
					</Button>
				</div>
			</div>
			<div className="grid w-full grid-cols-7 gap-1 px-1 hover:cursor-paint laptop:w-auto laptop:gap-1.5 laptop:px-0">
				<WeekDays />
				{dailyStatuses.map((status, idx) => (
					<RoomPriceRankCalendarDailyStatusItem
						key={`${status.date.value}_${idx}`}
						selectedPriceRank={selectedPriceRank}
						status={status}
						onSetPriceRank={onSetPriceRank}
					/>
				))}
			</div>
		</div>
	);
};

RoomPriceRankCalendar.displayName = "RoomPriceRankCalendar";
